import { ApplicationForm, blankForm, blankLocation, blankTradeRef, PrincipalEntry } from './types';

const s = (v: any): string => (v === null || v === undefined ? '' : String(v));
const yesNo = (v: boolean | null | undefined): '' | 'yes' | 'no' =>
  v === true ? 'yes' : v === false ? 'no' : '';

// Map the owner's stored application back into wizard form state for edit/resubmit.
// Sensitive values return as on-file flags + last4; those fields start empty.
export function hydrateForm(full: any): ApplicationForm {
  const f = blankForm();

  f.legalName = s(full.legalName);
  f.dba = s(full.dba);
  f.businessType = s(full.businessType);
  f.entityType = s(full.entityType);
  f.yearIncorporated = s(full.yearIncorporated);
  f.stateOfIncorporation = s(full.stateOfIncorporation);
  f.ein = s(full.ein);
  f.officeLine1 = s(full.officeLine1);
  f.officeLine2 = s(full.officeLine2);
  f.officeCity = s(full.officeCity);
  f.officeState = s(full.officeState);
  f.officeZip = s(full.officeZip);
  f.officePhone = s(full.officePhone);
  f.officeFax = s(full.officeFax);
  f.officeWebsite = s(full.officeWebsite);
  f.officeIsPrimary = Boolean(full.officeIsPrimary);
  f.officeIsHeadquarters = Boolean(full.officeIsHeadquarters);

  f.ownerFirstName = s(full.ownerFirstName);
  f.ownerLastName = s(full.ownerLastName);
  f.ownerTitle = s(full.ownerTitle);
  f.ownerMobile = s(full.ownerMobile);
  f.ownerEmail = s(full.ownerEmail);
  f.heardAboutUs = s(full.heardAboutUs);
  f.heardAboutUsDetail = s(full.heardAboutUsDetail);

  f.billingContact = s(full.billingContact);
  f.billingPhone = s(full.billingPhone);
  f.billingEmail = s(full.billingEmail);

  f.creditLine = s(full.creditLine);
  f.annualTonsRange = s(full.annualTonsRange);

  f.bankName = s(full.bankName);
  f.bankPhone = s(full.bankPhone);
  f.bankLine1 = s(full.bankLine1);
  f.bankCity = s(full.bankCity);
  f.bankState = s(full.bankState);
  f.bankZip = s(full.bankZip);
  f.bankContact = s(full.bankContact);
  f.bankAccountType = s(full.bankAccountType) || 'Business checking';

  f.hasBankruptcy = yesNo(full.hasBankruptcy);
  f.bankruptcyExplanation = s(full.bankruptcyExplanation);

  f.taxExempt = yesNo(full.taxExempt);
  f.resaleCertState = s(full.resaleCertState);
  f.resaleSellerType = s(full.resaleSellerType);
  f.resaleInBusinessOf = s(full.resaleInBusinessOf);
  f.resaleRegistrationNumber = s(full.resaleRegistrationNumber);
  f.resaleDescription = s(full.resaleDescription);

  f.tradeReferenceFileName = full.hasTradeReferenceAttachment ? 'Attached reference sheet' : '';
  f.signerTitle = s(full.signerTitle);

  const principals: PrincipalEntry[] = (full.principals ?? []).map((p: any) => ({
    fullName: s(p.fullName),
    title: s(p.title),
    percentOwned: s(p.percentOwned),
    homeLine1: s(p.homeLine1),
    homeLine2: s(p.homeLine2),
    homeCity: s(p.homeCity),
    homeState: s(p.homeState),
    homeZip: s(p.homeZip),
    ssn: '',
    ssnOnFile: Boolean(p.ssnOnFile),
    ssnLast4: s(p.ssnLast4),
  }));
  f.principals = principals.length ? principals : f.principals;

  const refs = (full.tradeReferences ?? []).map((r: any) => ({
    companyName: s(r.companyName),
    contactName: s(r.contactName),
    phone: s(r.phone),
    email: s(r.email),
    line1: s(r.line1),
    city: s(r.city),
    state: s(r.state),
    zip: s(r.zip),
  }));
  while (refs.length < 3) refs.push(blankTradeRef());
  f.references = refs;

  const locations = (full.locations ?? []).map((l: any) => ({
    name: s(l.name),
    line1: s(l.line1),
    line2: s(l.line2),
    city: s(l.city),
    state: s(l.state),
    zip: s(l.zip),
    deliveryNotes: s(l.deliveryNotes),
    isPrimary: false,
    isHeadquarters: false,
    sameAsPrevious: false,
  }));
  f.locations = locations.length ? locations : [blankLocation()];

  f.teamMembers = (full.teamMembers ?? []).map((m: any) => ({
    name: s(m.name),
    email: s(m.email),
    role: s(m.role) || 'PLACE_ORDERS',
    locationAccess: Array.isArray(m.locationAccess)
      ? m.locationAccess.map((n: any) => Number(n))
      : [],
  }));

  f.productInterestIds = Array.isArray(full.productInterestIds) ? full.productInterestIds : [];

  return f;
}
